/* ELC Shift — the spoken daily brief (v132.60).

   Matt: "i also want this read to me daily using the jack admin voice already
   wired in", then "i think this should come up for any admin when they log in,
   and they can cancel it if they dont want to listen", then "paid please".

   WHAT THIS IS: on the first login of a Dubai day, JACK reads the admin their
   own daily brief. A bar appears saying what is happening, with Stop, and with
   a way to turn it off for good. Nothing about it blocks the app.

   THE PAID VOICE, with the free one as a safety net. The Worker's /api/admin/tts
   proxy returns ElevenLabs audio and keeps the key server-side; if it is not
   configured, errors, or is out of quota, we fall through to the browser's own
   speech synthesis rather than saying nothing. Same chain the dev console uses.

   COST. This is the reason the brief text is deterministic. Every admin in the
   same scope gets byte-identical text on a given day, so the Worker's TTS cache
   serves one generation to all of them instead of billing per person. A model
   writing a slightly different sentence each time would defeat that completely,
   on top of being able to get a number wrong.

   ONCE A DAY, and only if they want it. Two separate switches, because they
   answer different questions:
     - "not now"  -> today is marked done; tomorrow it speaks again.
     - "never"    -> it never speaks again on this device until they clear it.
   Both are per-device localStorage, which is the honest scope for a preference
   about audio: it is about where you are sitting, not who you are.

   AUTOPLAY. Logging in is a user gesture, so audio is normally allowed. When a
   browser refuses anyway, the bar stays with a Play button rather than
   pretending it spoke. Silence must never look the same as success.
*/

const DONE_KEY = 'elcBriefSpokenDay';
const OFF_KEY = 'elcBriefSpokenOff';

let _audio = null;
// v133.86 (overnight sweep, L): which speech is current. Stop (and "Do not read") bumps it, so audio that
// was still LOADING when Stop was tapped never starts, and Play never runs alongside an earlier copy.
let _gen = 0;
let _spoke = false;   // once per page load, whatever else happens

// localStorage is per-device and can throw (private windows, blocked storage).
// Every read and write is guarded, and a failure means "we have not spoken
// today", which errs towards speaking rather than towards silence.
function _get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
function _set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }

// The Dubai day, so "once a day" lines up with the business day rather than
// with UTC. A brief at 1am Dubai is still today's.
export function dubaiDayStr(now) {
  const d = new Date((now || Date.now()) + 4 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

export function isOff() { return _get(OFF_KEY) === '1'; }
export function spokenToday(now) { return _get(DONE_KEY) === dubaiDayStr(now); }

export function turnOff() {
  _set(OFF_KEY, '1');
  stop();
  const bar = document.getElementById('sbBar');
  if (bar) bar.remove();
  if (typeof showToast === 'function') showToast('The daily briefing will not be read again on this device', 'success');
}

// Stop whatever is playing, both paths.
export function stop() {
  _gen++;
  if (_audio) { try { _audio.pause(); } catch (e) {} _audio = null; }
  try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (e) {}
}

function dismiss() {
  stop();
  const bar = document.getElementById('sbBar');
  if (bar) bar.remove();
}

// The bar. Deliberately plain and out of the way: it is an announcement, not a
// dialog, and it must never sit over a control somebody is reaching for.
function showBar(text, onPlay) {
  const old = document.getElementById('sbBar');
  if (old) old.remove();
  const bar = document.createElement('div');
  bar.id = 'sbBar';
  bar.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:9000;background:#0f172a;color:#e2e8f0;'
    + 'padding:11px 14px;display:flex;gap:10px;align-items:center;font-size:13px;'
    + 'box-shadow:0 -4px 16px rgba(0,0,0,.25)';
  const label = document.createElement('div');
  label.style.cssText = 'flex:1;min-width:0';
  label.textContent = onPlay ? 'Your daily briefing is ready.' : 'JACK is reading your daily briefing.';
  bar.appendChild(label);

  if (onPlay) {
    const play = document.createElement('button');
    play.textContent = 'Play';
    play.style.cssText = 'background:#0f766e;color:#fff;border:0;border-radius:8px;padding:7px 14px;font-size:13px;font-weight:700;cursor:pointer';
    play.onclick = function () { play.remove(); label.textContent = 'JACK is reading your daily briefing.'; onPlay(); };
    bar.appendChild(play);
  }

  const stopBtn = document.createElement('button');
  stopBtn.textContent = 'Stop';
  stopBtn.style.cssText = 'background:#1e293b;color:#e2e8f0;border:1px solid #334155;border-radius:8px;padding:7px 14px;font-size:13px;cursor:pointer';
  stopBtn.onclick = dismiss;
  bar.appendChild(stopBtn);

  const never = document.createElement('button');
  never.textContent = 'Do not read these to me';
  never.style.cssText = 'background:transparent;color:var(--tiny);border:0;font-size:12px;text-decoration:underline;cursor:pointer';
  never.onclick = turnOff;
  bar.appendChild(never);

  document.body.appendChild(bar);
  return bar;
}

// The free fallback. Returns true if it managed to start.
function browserSpeak(text, onend) {
  try {
    if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance === 'undefined') return false;
    const u = new window.SpeechSynthesisUtterance(text);
    u.rate = 1.05; u.pitch = 0.9;
    u.onend = onend; u.onerror = onend;
    window.speechSynthesis.speak(u);
    try { window.speechSynthesis.resume(); } catch (e) {}   // Chrome starts paused
    return true;
  } catch (e) { return false; }
}

// The paid voice, falling back to the free one. `cb` fires exactly once.
function speak(text, cb) {
  let done = false;
  const g = _gen;
  const finish = function () { if (done) return; done = true; if (cb) cb(); };
  const fallback = function () { if (g !== _gen) return; if (!browserSpeak(text, finish)) finish(); };

  if (typeof DB === 'undefined' || typeof fetch === 'undefined' || typeof Audio === 'undefined') { fallback(); return; }
  fetch(DB.workerUrl() + '/api/admin/tts', {
    method: 'POST', cache: 'no-store',
    headers: { 'Authorization': 'Bearer ' + DB.getToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: String(text).slice(0, 600) }),
  }).then(function (res) {
    const type = res.headers.get('content-type') || '';
    if (!res.ok || type.indexOf('audio') < 0) { fallback(); return; }
    return res.blob().then(function (b) {
      if (g !== _gen) return;   // stopped while it loaded
      const url = URL.createObjectURL(b);
      _audio = new Audio(url);
      _audio.onended = function () { try { URL.revokeObjectURL(url); } catch (e) {} finish(); };
      _audio.onerror = function () { try { URL.revokeObjectURL(url); } catch (e) {} fallback(); };
      const p = _audio.play();
      // A refused autoplay rejects here; the caller shows a Play button.
      if (p && typeof p.catch === 'function') p.catch(function () { fallback(); });
    });
  }).catch(fallback);
}

// The entry point, called at the end of showAdmin().
//
// Fails SILENTLY and completely: a briefing is a nicety, and nothing here may
// ever stop an admin getting into the app. Every branch returns rather than
// throwing, and the whole body is wrapped by the caller as well.
// v132.70: on demand, from the JACK tab. Ignores the once-a-day marker and
// the opt-out (the person just asked for it) and stops anything playing.
export function readBriefNow() { stop(); return speakBrief(true); }
export async function maybeSpeakDailyBrief() { return speakBrief(false); }
async function speakBrief(force) {
  try {
    if (!force && (_spoke || isOff() || spokenToday())) return;
    if (typeof DB === 'undefined' || !DB.getToken || !DB.getToken()) return;
    _spoke = true;

    const res = await fetch(DB.workerUrl() + '/api/admin/daily-brief-email/spoken', {
      cache: 'no-store', headers: { 'Authorization': 'Bearer ' + DB.getToken() },
    });
    if (!res.ok) return;
    const data = await res.json();
    const text = data && data.text;
    if (!text) return;

    // Mark the day BEFORE speaking. If audio is refused or the tab is closed
    // mid-sentence, the alternative is a briefing that restarts on every
    // navigation, which is worse than one missed line.
    _set(DONE_KEY, dubaiDayStr());

    let bar = showBar(text, null);
    speak(text, function () {
      const b = document.getElementById('sbBar');
      if (b) b.remove();
    });
    // If nothing is audible within a few seconds, offer a Play button rather
    // than leaving a bar claiming JACK is speaking when he is not.
    setTimeout(function () {
      const playing = (_audio && !_audio.paused) || (window.speechSynthesis && window.speechSynthesis.speaking);
      if (!playing && document.getElementById('sbBar')) {
        showBar(text, function () { stop(); speak(text, function () { dismiss(); }); });
      }
    }, 2500);
  } catch (e) { /* never block the app */ }
}

if (typeof window !== 'undefined') {
  window.maybeSpeakDailyBrief = maybeSpeakDailyBrief;
  window.spokenBriefStop = stop;
  window.spokenBriefTurnOff = turnOff;
  window.spokenBriefReadNow = readBriefNow;
}
